import { useMutation, useQueryClient } from '@tanstack/react-query';

import { BankLinkEventType, convertToTaxonomyCase, EVENTS, useIdentify, useTrack } from '@arrived/analytics';
import { postLinkPlaidExternalAccount } from '@arrived/api_v2';
import { trackMarketingConversionEvent } from '@arrived/marketing';
import {
  Account,
  ExternalAccount,
  MARKETING_CONVERSION_EVENTS,
  PlaidLinkExternalAccountRequest,
} from '@arrived/models';

import { UseMutationOptions } from '../../queries.utils';
import { accountsPrimaryKeyFn } from '../../users';
import { accountsKeyFn } from '../accounts.keys';

export function usePostExternalAccountLinkPlaidMutation(
  options?: UseMutationOptions<ExternalAccount, PlaidLinkExternalAccountRequest>
) {
  const track = useTrack();
  const identify = useIdentify();
  const queryClient = useQueryClient();

  return useMutation((request) => postLinkPlaidExternalAccount(request), {
    ...options,
    onSuccess: (data, variables, context) => {
      track(EVENTS.BANK_LINK, {
        update_type: BankLinkEventType.CREATE,
        verification_status: convertToTaxonomyCase(data.verified),
        connection_type: convertToTaxonomyCase(data.linkType),
      });
      identify({
        has_had_bank_account_linked: true,
      });

      trackMarketingConversionEvent({
        type: MARKETING_CONVERSION_EVENTS.LINKED_BANK,
      });

      const primaryAccount = queryClient.getQueryData<Account>(accountsPrimaryKeyFn());

      if (primaryAccount) {
        queryClient.setQueryData(accountsPrimaryKeyFn(), { ...primaryAccount, externalAccount: data });
      }

      const account = queryClient.getQueryData<Account>(accountsKeyFn(variables.accountId));

      if (account) {
        queryClient.setQueryData(accountsKeyFn(variables.accountId), { ...account, externalAccount: data });
      }

      queryClient.invalidateQueries(accountsPrimaryKeyFn());
      queryClient.invalidateQueries(accountsKeyFn(variables.accountId));

      options?.onSuccess?.(data, variables, context);
    },
  });
}
